// src/pages/CompanyList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaFilter,
  FaTimesCircle,
  FaBuilding,
  FaPlus,
} from "react-icons/fa";
import { FiRotateCcw } from "react-icons/fi";

import { useGetAllCompaniesQuery } from "../toolkitServices/companyApi";
import { useDebounce } from "../hooks/useDebounced";
import { useSelector } from "react-redux";

/* ------------ Tiny reusable spinner (SalesList style) ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const canDelete = (user) => {
  const perm = user?.permissions?.find((p) => p.moduleKey === "quotation");
  return user?.role === "owner" || perm?.permissions?.delete;
};
const CompanyList = () => {
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const { user } = useSelector((state) => state.auth);

  // search + debounce
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 350);

  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // filter modal
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // If you REALLY want only 1 company allowed, enable this:
  // const canCreate = !rows?.length;
  const canCreate = true;

  const queryArgs = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || "",
    }),
    [page, limit, debouncedSearch]
  );

  const { data, isLoading, isFetching, error } = useGetAllCompaniesQuery(queryArgs, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const rows = data?.data || [];
  const total = data?.total ?? rows.length;
  const pages = data?.pages ?? Math.max(1, Math.ceil((total || 0) / limit));
  const isDeleteAllowed = canDelete(user);

  // Ctrl+P => create | Ctrl+F => focus search
  useEffect(() => {
    const onKeyDown = (e) => {
      const isCtrlP = e.ctrlKey && (e.key === "p" || e.key === "P");
      const isCtrlF = e.ctrlKey && (e.key === "f" || e.key === "F");
      if (isCtrlP) {
        e.preventDefault();
        if (canCreate && !rows.length) {
          navigate("/organisation/company-master-list/new-company");
        }
      }

      if (isCtrlF) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };

      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate, canCreate, rows.length]);

  // search change => page 1
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // chips
  const chips = useMemo(() => {
    const arr = [];
    if (search?.trim()) arr.push({ key: "search", label: `Search: ${search.trim()}` });
    return arr;
  }, [search]);

  const clearChip = (key) => {
    if (key === "search") setSearch("");
  };

  const resetFilters = () => {
    setSearch("");
  };

  const paginationSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 32,
      height: 32,
      borderRadius: 6,
      borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
      boxShadow: state.isFocused
        ? "0 0 0 1px rgba(148,129,206,.25)"
        : "none",
      fontSize: 10,
    }),
    valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
    menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
    option: (base, state) => ({
      ...base,
      padding: "6px 8px",
      backgroundColor: state.isSelected
        ? "#9481ce"
        : state.isFocused
          ? "#ede9fe"
          : "white",
      color: state.isSelected ? "white" : "#334155",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="relative flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-2xl bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {/* Global overlay loader */}
      {isLoading && !rows.length && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-slate-600 text-xs sm:text-sm">
            <Spinner size={22} />
            <span>Loading organisations…</span>
          </div>
        </div>
      )}

      {/* Header bar */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <h2 className="flex min-w-0 items-center gap-2 truncate text-[15px] font-semibold tracking-tight sm:text-lg">
            <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-xl bg-white/15 text-xs">
              <FaBuilding />
            </span>
            <span className="truncate">
              Organisations {isFetching ? "…" : total ? `(${total})` : ""}
            </span>
          </h2>

          {isFetching && rows.length > 0 && (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] sm:inline-flex">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        {!rows?.length ? (
        <div className="flex items-center gap-2">
         
          <span className="hidden lg:inline text-[11px] text-violet-100/90">
            Ctrl + P to create new
          </span>
          

          {canCreate && (
            <Link
              to="/organisation/company-master-list/new-company"
              className="inline-flex h-9 items-center gap-2 rounded-xl bg-white px-3 text-[11px] font-semibold text-[#6b4fd8] shadow-sm transition hover:bg-violet-50 sm:rounded-full"
              title="Ctrl + P"
            >
              <FaPlus className="text-[10px]" />
              <span className="hidden sm:inline">Add New</span>
              <span className="sm:hidden">Add</span>
            </Link>
          )}
        </div>
        ) : ""}
      </div>

      {/* Filters bar (mobile vs desktop) */}
      <div className="shrink-0 border-b bg-slate-50/80 px-3 py-2 sm:px-4">
        {/* MOBILE */}
        <div className="flex items-center gap-2 sm:hidden">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search company / city / state…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400"
          />
          <button
            onClick={() => setIsFilterOpen(true)}
            className="inline-flex h-9 flex-shrink-0 items-center gap-1 rounded-lg border border-violet-100 bg-white px-3 text-[11px] font-semibold text-[#6b59c6] shadow-sm hover:bg-violet-50"
            title="Filters"
          >
            <FaFilter /> Filters
          </button>
        </div>

        {/* DESKTOP */}
        <div className="hidden items-end gap-2 sm:flex">
          <div className="flex min-w-[240px] flex-1 flex-col gap-[1px]">
            <label className="text-[11px] font-medium text-slate-600">Search</label>
            <input
              ref={searchRef}
              type="text"
              placeholder="Company / city / state…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          <div className="flex flex-col gap-[1px] w-auto">
            <label className="text-[11px] font-medium text-slate-600 invisible">
              Filters
            </label>
            <button
              onClick={() => setIsFilterOpen(true)}
              className="inline-flex h-9 items-center gap-1 rounded-lg border border-violet-100 bg-white px-3 text-[11px] font-semibold text-[#6b59c6] shadow-sm hover:bg-violet-50"
            >
              <FaFilter /> Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error strip */}
      {error && (
        <div className="shrink-0 border-b bg-red-50 px-3 py-1.5 text-xs text-red-600 sm:px-4">
          {error?.data?.message || error?.data?.msg || "Failed to load companies"}
        </div>
      )}

      {/* Chips */}
      {chips.length > 0 && (
        <div className="flex shrink-0 items-center gap-2 border-b bg-slate-50 px-3 py-1.5 sm:px-4">
          <div className="flex min-w-0 flex-nowrap items-center gap-2 overflow-x-auto pr-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {chips.map((c) => (
              <span
                key={c.key}
                className="inline-flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full border bg-white px-2 py-1 text-[11px] shadow-sm"
              >
                {c.label}
                <button
                  className="text-slate-500 hover:text-red-500"
                  onClick={() => clearChip(c.key)}
                  title="Clear"
                >
                  <FaTimesCircle />
                </button>
              </span>
            ))}
          </div>

          <button
            className="ml-auto flex-shrink-0 text-[11px] text-[#6b59c6] hover:underline"
            onClick={resetFilters}
            title="Reset all"
          >
            Reset
          </button>
        </div>
      )}

      {/* Table */}
      <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain bg-white [-webkit-overflow-scrolling:touch]">
        <table className="w-full min-w-[900px] text-left text-xs sm:text-sm">
<thead className="sticky top-0 z-40 bg-[#efeafd] text-[10px] uppercase text-[#4b3d91] shadow-[0_1px_0_rgba(148,129,206,.18)] sm:text-[11px]">
            <tr>
              <th className="px-3 py-2 sm:px-4">#</th>
              <th className="px-3 py-2 sm:px-4">Company Name</th>
              <th className="px-3 py-2 sm:px-4">Short Name</th>
              <th className="px-3 py-2 sm:px-4">Contact</th>
              <th className="px-3 py-2 sm:px-4">City</th>
              <th className="px-3 py-2 sm:px-4">State</th>
<th className="sticky right-0 top-0 z-50 w-[82px] min-w-[82px] border-l border-violet-200 bg-[#ddd5f7] px-1 py-2 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[104px] sm:min-w-[104px] sm:px-2">
                Action
              </th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            {/* skeleton rows (when fetching but we already have rows, keep subtle) */}
            {isLoading &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-t border-slate-100">
                  {Array.from({ length: 6 }).map((__, j) => (
                    <td key={j} className="px-3 py-2.5 sm:px-4">
                      <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
                    </td>
                  ))}
                  <td className="sticky right-0 bg-white px-1.5 py-2.5 text-center shadow-[-8px_0_12px_-12px_rgba(15,23,42,.35)]">
                    <div className="mx-auto h-7 w-12 animate-pulse rounded-full bg-gray-200" />
                  </td>
                </tr>
              ))}

            <AnimatePresence>
              {!isLoading && rows.length ? (
                rows.map((item, index) => {
                  const id = item?._id;
                  return (
                    <motion.tr
                      key={id || index}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.18, delay: index * 0.01 }}
                      className="group border-t border-slate-100 transition hover:bg-[#f7f5ff]"
                    >
                      <td className="px-3 py-2.5 font-medium sm:px-4">
                        {(page - 1) * limit + index + 1}
                      </td>

                      <td className="max-w-[260px] px-3 py-2.5 sm:px-4">
                        <span className="block truncate" title={item?.companyName || ""}>
                          {item?.companyName || "—"}
                        </span>
                      </td>

                      <td className="px-3 py-2.5 sm:px-4">{item?.shortName || "—"}</td>
                      <td className="px-3 py-2.5 sm:px-4">{item?.contact || "—"}</td>
                      <td className="px-3 py-2.5 sm:px-4">{item?.city || "—"}</td>
                      <td className="px-3 py-2.5 sm:px-4">{item?.state || "—"}</td>

<td className="sticky right-0 z-10 w-[82px] min-w-[82px] whitespace-nowrap border-l border-violet-100 bg-violet-50 px-1 py-2 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[104px] sm:min-w-[104px] sm:px-2">
                        <div className="inline-flex items-center justify-center gap-0.5 sm:gap-1">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[11px] text-[#9481ce] hover:bg-[#9481ce]/15"
                          title="Edit"
                          onClick={() =>
                            navigate(`/organisation/company-master-list/${id}/edit`)
                          }
                        >
                          <FaPencilAlt />
                        </motion.button>

                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[11px] text-rose-500 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40"
                          title={isDeleteAllowed ? "Delete" : "No delete permission"}
                          disabled={!isDeleteAllowed}
                          // Hook up delete later (ConfirmDialog + mutation)
                        >
                          <FaTrashAlt />
                        </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              ) : !isLoading ? (
                <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <td colSpan={7} className="py-8 text-center text-gray-500">
                    No matching records found.
                  </td>
                </motion.tr>
              ) : null}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex shrink-0 items-center justify-between gap-2 rounded-b-2xl border-t bg-slate-50/60 px-2 py-1.5 text-[10px] sm:px-4 sm:py-2 sm:text-sm">
        <div className="hidden text-slate-600 sm:block">
          Page <span className="font-semibold">{page}</span> of{" "}
          <span className="font-semibold">{pages}</span> • {total ?? 0} record(s)
        </div>

        <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
          <span className="hidden text-slate-500 sm:inline">Rows / page</span>
          <Select
            value={{ value: limit, label: `${limit}` }}
            onChange={(opt) => {
              setLimit(opt?.value || 10);
              setPage(1);
            }}
            options={[10, 20, 50].map((value) => ({ value, label: `${value}` }))}
            styles={paginationSelectStyles}
            isSearchable={false}
            menuPortalTarget={document.body}
            className="w-[58px] sm:w-[70px]"
          />
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-violet-50 disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            <span className="sm:hidden">&lsaquo;</span><span className="hidden sm:inline">Prev</span>
          </button>
          <span className="flex items-center gap-1 text-slate-600 sm:hidden">
            <Select value={{ value: pages ? page : 1, label: String(pages ? page : 1) }} options={Array.from({ length: Math.max(1, pages) }, (_, index) => ({ value: index + 1, label: String(index + 1) }))} onChange={(option) => setPage(Number(option?.value) || 1)} isSearchable={false} menuPlacement="top" menuPosition="fixed" menuPortalTarget={document.body} menuShouldScrollIntoView={false} styles={paginationSelectStyles} className="w-[52px]" aria-label="Select page number" />
            <span>/ {Math.max(1, pages)}</span>
          </span>
          <span className="hidden items-center gap-1 text-slate-600 sm:flex">
            <span>Page</span>
            <Select value={{ value: pages ? page : 1, label: String(pages ? page : 1) }} options={Array.from({ length: Math.max(1, pages) }, (_, index) => ({ value: index + 1, label: String(index + 1) }))} onChange={(option) => setPage(Number(option?.value) || 1)} isSearchable={false} menuPlacement="top" menuPosition="fixed" menuPortalTarget={document.body} menuShouldScrollIntoView={false} styles={paginationSelectStyles} className="w-[58px]" aria-label="Select page number" />
            <span>of {Math.max(1, pages)}</span>
          </span>
          <button
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-violet-50 disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages}
          >
            <span className="sm:hidden">&rsaquo;</span><span className="hidden sm:inline">Next</span>
          </button>
        </div>
      </div>

      {/* Filters Modal (SalesList style) */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Filters</h3>
                  <p className="text-[11px] text-slate-500">
                    Search organisations by company, city or state.
                  </p>
                </div>
                <button
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setIsFilterOpen(false)}
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 space-y-4 text-[11px] overflow-y-auto">
                <div>
                  <label className="block text-slate-600 mb-1">Search</label>
                  <input
                    ref={searchRef}
                    type="text"
                    placeholder="Company / city / state…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                  />
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw />
                  Reset
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompanyList;
